import { useEffect, useRef, useState } from 'react';
import { Check } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

const ROWS = [
  'Extension installed',
  'AI agent connected',
  'First save',
  'First note',
];

/**
 * The final beat of onboarding: a celebratory reveal, a paced read, and
 * then a quiet auto-redirect. No escape hatch — the page IS the moment.
 *
 * Stages (all relative to mount):
 *    80ms     Brand check + ring scale-in.
 *   180ms     Headline + subtitle fade in (slight cascade below the icon).
 *   460ms     Row 1 reveals.
 *  +280ms ea  Row 2..4 → all four rows visible by ~1300ms.
 *  4500ms     "Taking you to your saves…" footer fades in.
 *  5800ms     Auto-redirect.
 *
 * The 3.2s window between full reveal (~1300ms) and footer (~4500ms) is
 * the deliberate "read it" pause. The further 1.3s after the footer
 * appears is the "we're moving" beat before the redirect actually fires.
 *
 * Bug fixes vs. earlier iterations:
 *   - onComplete is captured via ref so non-memoised parent callbacks
 *     can't restart the timers on every render (was causing visible
 *     jitter and rare double-redirects).
 *   - The unmount cleanup clears every pending timer.
 *   - finishedRef guards double-fire even though the only fire path is
 *     the auto-timer (no Continue button anymore).
 */
interface SuccessAnimationProps {
  onComplete?: () => void;
}

const ICON_REVEAL_MS = 80;
const HEADLINE_REVEAL_MS = 180;
const ROW_START_MS = 380;
const ROW_STAGGER_MS = 220;
// All four rows visible at ROW_START_MS + 4 × ROW_STAGGER_MS = ~1260ms.
// Footer fades in at 3000ms (~1.7s reading pause after full reveal), then
// the redirect fires 800ms after that. Total ~3.8s — paced for reading
// but not lingering.
const FOOTER_FADE_MS = 3000;
const COMPLETE_MS = 3800;

export function SuccessAnimation({ onComplete }: SuccessAnimationProps) {
  const [revealedRows, setRevealedRows] = useState(0);
  const [iconIn, setIconIn] = useState(false);
  const [headlineIn, setHeadlineIn] = useState(false);
  const [footerIn, setFooterIn] = useState(false);
  const finishedRef = useRef(false);

  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setIconIn(true), ICON_REVEAL_MS));
    timers.push(setTimeout(() => setHeadlineIn(true), HEADLINE_REVEAL_MS));
    ROWS.forEach((_, i) => {
      timers.push(
        setTimeout(() => setRevealedRows(i + 1), ROW_START_MS + i * ROW_STAGGER_MS),
      );
    });
    timers.push(setTimeout(() => setFooterIn(true), FOOTER_FADE_MS));
    timers.push(
      setTimeout(() => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        onCompleteRef.current?.();
      }, COMPLETE_MS),
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-white">
      {/* Ambient brand-blue radial wash behind the content. Anchored slightly
          above center so the icon sits in the brightest spot. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 40%, rgba(37,99,235,0.10), rgba(37,99,235,0) 55%)',
        }}
      />

      <div className="relative mx-auto w-full max-w-md px-6 text-center">
        {/* Icon block — circular brand-blue badge with a soft outer ring that
            scales in alongside the badge for a single coherent reveal beat.   */}
        <div className="relative mx-auto mb-7 flex h-20 w-20 items-center justify-center">
          <span
            aria-hidden="true"
            className={cn(
              'absolute inset-0 rounded-full bg-blue-600/15 transition-all duration-700 ease-out',
              iconIn ? 'scale-110 opacity-100' : 'scale-75 opacity-0',
            )}
          />
          <span
            aria-hidden="true"
            className={cn(
              'absolute inset-0 rounded-full ring-1 ring-blue-600/20 transition-all duration-700 ease-out',
              iconIn ? 'scale-150 opacity-100' : 'scale-100 opacity-0',
            )}
          />
          <span
            className={cn(
              'relative flex h-16 w-16 items-center justify-center rounded-full bg-blue-600 text-white shadow-md shadow-blue-600/25 transition-all duration-500 ease-out',
              iconIn ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
            )}
          >
            <Check size={30} weight="bold" />
          </span>
        </div>

        {/* Headline + subtitle */}
        <div
          className={cn(
            'transition-all duration-500 ease-out',
            headlineIn ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
          )}
        >
          <h1 className="text-3xl font-light tracking-tight text-neutral-900">
            You're all set.
          </h1>
          <p className="mt-2 text-sm text-neutral-500">
            Welcome to Solem.
          </p>
        </div>

        {/* Checklist — block-level so it doesn't collide with siblings. The
            inner ul is inline-flex so it shrink-wraps and centers cleanly
            under the headline. */}
        <div className="mt-10 flex justify-center">
          <ul className="inline-flex flex-col items-start gap-3 text-left">
            {ROWS.map((row, i) => (
              <li
                key={row}
                className={cn(
                  'flex items-center gap-3 transition-all duration-500 ease-out',
                  i < revealedRows
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-2 opacity-0',
                )}
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white">
                  <Check size={13} weight="bold" />
                </span>
                <span className="text-sm font-medium text-neutral-900">
                  {row}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Quiet status footer. Fades in only once the user has had time to
            read the recap — and then sits there for ~1.3s before the
            auto-redirect actually fires. */}
        <p
          className={cn(
            'mt-12 text-[11px] font-medium uppercase tracking-[0.18em] text-neutral-400 transition-opacity duration-700 ease-out',
            footerIn ? 'opacity-100' : 'opacity-0',
          )}
        >
          Taking you to your saves
          <span className="ml-1 inline-flex w-3 justify-start">
            <Dots active={footerIn} />
          </span>
        </p>
      </div>

      {/* Local keyframes — kept inline so the file remains self-contained. */}
      <style>{`
        @keyframes solem-dots {
          0%, 20%   { opacity: 0; }
          50%       { opacity: 1; }
          100%      { opacity: 0; }
        }
        .solem-dot {
          animation: solem-dots 1.4s infinite ease-in-out;
        }
        .solem-dot:nth-child(2) { animation-delay: 0.2s; }
        .solem-dot:nth-child(3) { animation-delay: 0.4s; }
        @media (prefers-reduced-motion: reduce) {
          .solem-dot { animation: none; opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function Dots({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <span aria-hidden="true" className="inline-flex gap-0.5">
      <span className="solem-dot">.</span>
      <span className="solem-dot">.</span>
      <span className="solem-dot">.</span>
    </span>
  );
}

